import InterviewThreadApi from "./InterviewThreadApi";
import VoiceRecorder, {TVoiceRecording} from "./VoiceRecorder";
import VoiceSilenceDetector from "./VoiceSilenceDetector";

export type TVoiceInputState = "idle"|"requestingPermission"|"recording"|"transcribing"|"failed";

export default class VoiceInputController {

    private state: TVoiceInputState = "idle";
    private error: string|null = null;
    private recordingLevel = 0;
    private destroyed = false;
    private operationGeneration = 0;
    private transcriptionController: AbortController|null = null;

    constructor(
        private readonly api: InterviewThreadApi,
        private readonly threadId: number,
        private readonly enabled: boolean,
        private readonly translate: (key: string) => string,
        private readonly applyTranscription: (text: string) => void,
        private readonly stopPlayback: () => void,
        private readonly onChange: () => void,
        private readonly recorder = new VoiceRecorder(),
        private readonly silenceDetector = new VoiceSilenceDetector(),
    ) {
    }

    public isSupported(): boolean {
        return this.enabled && this.recorder.isSupported();
    }

    public getState(): TVoiceInputState {
        return this.state;
    }

    public getError(): string|null {
        return this.error;
    }

    public getRecordingLevel(): number {
        return this.recordingLevel;
    }

    public clearError(): void {
        if (this.state === "failed") {
            this.state = "idle";
        }
        this.error = null;
    }

    public async toggle(canStart: boolean): Promise<void> {
        if (this.state === "recording") {
            this.recorder.stop();
            return;
        }

        if (this.state === "requestingPermission"
            || this.state === "transcribing"
            || !canStart
            || !this.isSupported()
        ) {
            return;
        }

        await this.start();
    }

    public destroy(): void {
        this.destroyed = true;
        this.operationGeneration++;
        this.recordingLevel = 0;
        this.transcriptionController?.abort();
        this.transcriptionController = null;
        this.silenceDetector.stop();
        this.recorder.destroy();
    }

    private async start(): Promise<void> {
        this.clearError();
        this.stopPlayback();
        const generation = ++this.operationGeneration;
        this.state = "requestingPermission";
        this.onChange();

        try {
            const stream = await this.recorder.start(
                recording => void this.onStopped(recording),
                () => this.fail("interview_thread_transcription_failed"),
            );
            if (this.destroyed || generation !== this.operationGeneration) {
                this.recorder.destroy();
                return;
            }

            this.state = "recording";
            this.recordingLevel = 0;
            this.silenceDetector.start(
                stream,
                () => this.recorder.stop(),
                level => {
                    if (this.destroyed || this.state !== "recording") {
                        return;
                    }

                    this.recordingLevel = level;
                    this.onChange();
                },
            );
        } catch (error) {
            if (this.destroyed || generation !== this.operationGeneration) {
                return;
            }
            console.error(error);
            this.fail("interview_thread_voice_permission_denied");
        }
        this.onChange();
    }

    private async onStopped(recording: TVoiceRecording|null): Promise<void> {
        this.silenceDetector.stop();
        if (this.destroyed) {
            return;
        }

        if (recording === null) {
            this.fail("interview_thread_transcription_failed");
            return;
        }

        this.state = "transcribing";
        this.error = null;
        this.recordingLevel = 0;
        const requestController = new AbortController();
        this.transcriptionController = requestController;
        this.onChange();

        try {
            const response = await this.api.transcribeVoiceMessage(
                this.threadId,
                recording.audio,
                recording.filename,
                requestController.signal,
            );
            if (this.destroyed || requestController.signal.aborted) {
                return;
            }

            const text = response.text?.trim() ?? "";
            if (!response.success || text === "") {
                this.state = "failed";
                this.error = response.error || this.translate("interview_thread_transcription_failed");
                return;
            }

            this.applyTranscription(text);
            this.state = "idle";
        } catch (error) {
            if (this.destroyed) {
                return;
            }

            console.error(error);
            this.fail("interview_thread_transcription_failed", false);
        } finally {
            if (this.transcriptionController === requestController) {
                this.transcriptionController = null;
            }
            if (!this.destroyed) {
                this.onChange();
            }
        }
    }

    private fail(translationKey: string, redraw = true): void {
        this.silenceDetector.stop();
        this.state = "failed";
        this.error = this.translate(translationKey);
        this.recordingLevel = 0;
        if (redraw) {
            this.onChange();
        }
    }

}
